"use client"

import { useEffect, useState } from "react"
import type { Dispatch, SetStateAction } from "react"
import type { BooruPost } from "@/lib/booru-api"

// Moved out of components/booru-detail-modal.tsx (same state, same effect, same dependencies).
export function useGalleryContent({
  open,
  post,
  setCurrentAlbumPage,
}: {
  open: boolean
  post: BooruPost | null
  setCurrentAlbumPage: Dispatch<SetStateAction<number>>
}) {
  // State for fetched Doujins gallery pages
  const [fetchedDoujinsPages, setFetchedDoujinsPages] = useState<string[] | null>(null)
  const [fetchedDoujinsTitle, setFetchedDoujinsTitle] = useState<string | null>(null)
  // True while resolving an erome placeholder post's real gallery URLs (see the erome
  // lazy-load effect below). "Add to Editor" must wait for this — otherwise it grabs
  // post.fileUrl/sampleUrl before they're resolved, which for a placeholder post is still
  // just the search-result thumbnail, not the actual video/image. This is the difference
  // between clicking through fast (e.g. straight from Favorites/Goonfolio's single-button
  // linking flow) and idly browsing long enough for the fetch to finish in the background.
  // Gates the "Add to Editor" button while a lazy-loaded gallery (Erome placeholder posts,
  // or Doujins posts opened without albumImages) is still fetching its real content —
  // otherwise a fast click grabs the search-result thumbnail/placeholder instead of the
  // actual resolved media. Covers both sources; name kept generic since either can set it.
  const [isResolvingGalleryContent, setIsResolvingGalleryContent] = useState(false)

  // Fetch Doujins gallery pages when modal opens without albumImages
  useEffect(() => {
    console.log('[BooruDetailModal v50] Doujins fetch check:', {
      open,
      postId: post?.id,
      site: post?.site,
      hasAlbumImages: !!(post?.albumImages && post?.albumImages.length > 0),
      albumImagesLength: post?.albumImages?.length,
      hasGalleryUrl: !!post?.rawPost?.galleryUrl,
      galleryUrl: post?.rawPost?.galleryUrl,
      rawPost: post?.rawPost
    })
    
    if (!open || !post) {
      // Reset fetched pages when modal closes
      setFetchedDoujinsPages(null)
      setFetchedDoujinsTitle(null)
      setIsResolvingGalleryContent(false)
      return
    }

    // Reset before re-checking below — otherwise a stale "resolving" flag from a previous
    // placeholder post would linger onto a post that doesn't need this fetch at all.
    setIsResolvingGalleryContent(false)

    // Erome lazy-load: only for placeholder posts (eager fetch failed / beyond limit).
    // rawPost.needsDetailFetch is set exclusively on those — not on successfully
    // eagerly-fetched posts, so this never fires mid-play for single-video posts.
    if (post.site === 'erome' && post.rawPost?.needsDetailFetch && post.source) {
      // fetchedDoujinsPages is shared, cross-post state (effectiveAlbumImages
      // above reads `fetchedDoujinsPages ?? post.albumImages`), and this modal
      // can move from one post to the next without ever hitting the `!open ||
      // !post` reset above. Clearing it here, before the fetch, means a
      // failure (network error, or erome.com 429 rate-limiting us — seen live)
      // falls back to this post's own (empty) albumImages, i.e. the low-res
      // thumbnail. Without this, `return` on failure left whatever the
      // PREVIOUS post's fetchedDoujinsPages was in place, so the modal showed
      // an entirely different gallery's pages — wrong content, wrong page
      // count, navigation that didn't match what was on screen — under the
      // new post's identity. Reported as an erome album "being treated as a
      // doujin" with content that didn't match and couldn't be navigated.
      setFetchedDoujinsPages(null)
      setFetchedDoujinsTitle(null)
      setIsResolvingGalleryContent(true)
      const fetchEromeGallery = async () => {
        try {
          const res = await fetch(`/api/booru/erome/gallery?url=${encodeURIComponent(post.source!)}`)
          if (!res.ok) {
            // An erome placeholder's own fileUrl is its /thumbs/ URL, so a
            // failure here leaves the modal showing a low-res thumbnail
            // (now that fetchedDoujinsPages is cleared above, not some
            // other post's gallery) with nothing in the console — reported
            // as "some images show up as thumbnails".
            console.error('[BooruDetailModal] Erome gallery fetch failed:', res.status, post.source)
            return
          }
          const data = await res.json()
          const urls = (data.pages || []).map((p: any) => p.imageUrl || p.url).filter(Boolean) as string[]
          if (urls.length > 0) {
            setFetchedDoujinsPages(urls)
            setCurrentAlbumPage(0)
          } else {
            console.warn('[BooruDetailModal] Erome gallery returned no pages — staying on thumbnail:', post.source)
          }
        } catch (err) {
          console.error('[BooruDetailModal] Erome gallery fetch threw:', err, post.source)
        } finally {
          setIsResolvingGalleryContent(false)
        }
      }
      fetchEromeGallery()
      return
    }

    // Only for Doujins posts with galleryUrl but no albumImages
    if (post.site === 'doujins' && post.rawPost?.galleryUrl && (!post.albumImages || post.albumImages.length === 0)) {
      console.log('[BooruDetailModal v50] Doujins post opened without albumImages, fetching gallery pages:', {
        id: post.id,
        galleryUrl: post.rawPost.galleryUrl,
        albumMediaCount: post.albumMediaCount
      })

      // Same click-before-fetch-resolves race as the erome branch above — gate the
      // "Add to Editor" button here too, otherwise a fast click hands the editor the
      // unresolved placeholder (single cover image) instead of the real gallery pages.
      //
      // Also same stale-cross-post-state hazard as the erome branch: clear
      // fetchedDoujinsPages before fetching, not just on success, so a failed
      // fetch here falls back to this post's own (empty) albumImages instead
      // of leaving a PREVIOUSLY viewed post's pages on screen under this
      // post's identity.
      setFetchedDoujinsPages(null)
      setFetchedDoujinsTitle(null)
      setIsResolvingGalleryContent(true)
      const fetchGalleryPages = async () => {
        try {
          const response = await fetch(`/api/booru/doujins/gallery?url=${encodeURIComponent(post.rawPost.galleryUrl)}`)

          if (!response.ok) {
            console.error('[BooruDetailModal v50] Failed to fetch gallery pages:', response.status)
            return
          }

          const galleryDetail = await response.json()
          console.log('[BooruDetailModal v50] Fetched gallery pages:', {
            pageCount: galleryDetail.pages.length,
            hasAudio: galleryDetail.hasAudio,
            title: galleryDetail.title
          })

          // Extract image URLs from pages
          const imageUrls = galleryDetail.pages.map((page: any) => page.imageUrl)

          // Update state with fetched pages and title
          setFetchedDoujinsPages(imageUrls)
          setFetchedDoujinsTitle(galleryDetail.title || null)
          setCurrentAlbumPage(0)
        } catch (error) {
          console.error('[BooruDetailModal v50] Error fetching gallery pages:', error)
        } finally {
          setIsResolvingGalleryContent(false)
        }
      }

      fetchGalleryPages()
    } else {
      // Reset fetched pages if not a Doujins post or if it already has albumImages
      setFetchedDoujinsPages(null)
      setFetchedDoujinsTitle(null)
    }
  }, [open, post?.id, post?.site, post?.source, post?.rawPost?.galleryUrl, post?.rawPost?.needsDetailFetch, post?.albumImages])

  return { fetchedDoujinsPages, fetchedDoujinsTitle, isResolvingGalleryContent }
}
